<template>
  <div>
    <!-- In Impostazioni compare solo il riquadro di lancio: l'elenco vive in un dialog -->
    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-icon left class="mr-2">mdi-file-document-multiple-outline</v-icon>
        {{ t('admConfig.skillsEditor.title') }}
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-medium-emphasis mb-4">
          {{ t('admConfig.skillsEditor.description') }}
        </div>
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn color="primary" prepend-icon="mdi-file-document-edit-outline" @click="openList">
            {{ t('admConfig.skillsEditor.openEditor') }}
          </v-btn>
          <span v-if="skills.length" class="text-caption text-medium-emphasis">
            {{ t('admConfig.skillsEditor.skillCount', { count: skills.length }) }}
          </span>
        </div>
      </v-card-text>
    </v-card>

    <!-- Elenco delle skill, editabili una alla volta -->
    <v-dialog v-model="listDialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-file-document-multiple-outline</v-icon>
          {{ t('admConfig.skillsEditor.title') }}
          <v-spacer />
          <!-- Passaggio fra database: si esporta da uno (es. locale) e si importa nell'altro -->
          <v-btn
            variant="text"
            prepend-icon="mdi-download"
            :loading="exporting"
            :title="t('admConfig.skillsEditor.exportHint')"
            @click="exportAll"
          >
            {{ t('admConfig.skillsEditor.export') }}
          </v-btn>
          <v-btn
            variant="text"
            prepend-icon="mdi-upload"
            :title="t('admConfig.skillsEditor.importHint')"
            @click="fileInput?.click()"
          >
            {{ t('admConfig.skillsEditor.import') }}
          </v-btn>
          <input ref="fileInput" type="file" accept=".json,application/json" hidden @change="onImportFile" />
          <v-btn icon variant="text" :loading="loading" :title="t('admConfig.skillsEditor.reload')" @click="load">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon variant="text" @click="listDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 75vh;">
          <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">
            {{ error }}
          </v-alert>
          <v-alert v-if="importDone" type="success" variant="tonal" density="compact" closable class="mb-3"
                   @click:close="importDone = ''">
            {{ importDone }}
          </v-alert>

          <!-- Skill nuova: id, poi si apre subito l'editor con lo scheletro -->
          <v-sheet rounded border class="pa-3 mb-4">
            <div class="text-subtitle-2 mb-2">{{ t('admConfig.skillsEditor.newTitle') }}</div>
            <div class="d-flex align-start flex-wrap" style="gap: 12px;">
              <v-text-field
                v-model="newSkillId"
                :label="t('admConfig.skillsEditor.newId')"
                :hint="t('admConfig.skillsEditor.newIdHint')"
                :error-messages="newSkillError"
                density="compact"
                persistent-hint
                style="min-width: 280px; flex: 1;"
                @keyup.enter="createSkill"
              />
              <v-btn
                color="primary"
                variant="flat"
                prepend-icon="mdi-plus"
                :disabled="!newSkillId.trim()"
                @click="createSkill"
              >
                {{ t('admConfig.skillsEditor.create') }}
              </v-btn>
            </div>
          </v-sheet>

          <v-text-field
            v-model="filter"
            :label="t('admConfig.skillsEditor.filter')"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            clearable
            hide-details
            class="mb-3"
          />

          <div v-if="loading && !skills.length" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else-if="!filteredSkills.length" class="text-body-2 text-medium-emphasis py-4">
            {{ t('admConfig.skillsEditor.empty') }}
          </div>

          <v-table v-else density="compact" class="skills-table">
            <thead>
              <tr>
                <th>{{ t('admConfig.skillsEditor.colId') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="skill in filteredSkills" :key="skill.id">
                <td><code>{{ skill.id }}</code></td>
                <td>
                  <div class="d-flex align-center" style="gap: 8px;">
                    <v-btn
                      size="small"
                      variant="tonal"
                      color="primary"
                      prepend-icon="mdi-file-document-edit-outline"
                      @click="openEditor(skill.id)"
                    >
                      {{ t('admConfig.skillsEditor.edit') }}
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="text"
                      prepend-icon="mdi-history"
                      @click="openHistory(skill.id)"
                    >
                      {{ t('admConfig.skillsEditor.history') }}
                    </v-btn>
                    <span class="text-caption text-medium-emphasis">
                      {{ t('admConfig.skillEditor.size', { kb: ((skill.sizeBytes || 0) / 1024).toFixed(1) }) }}
                      · {{ formatDateTime(skill.modifiedAt) }}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Anteprima dell'import: si sceglie cosa sovrascrivere prima di salvare -->
    <v-dialog v-model="importDialog.open" max-width="900" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-upload</v-icon>
          {{ t('admConfig.skillsEditor.importTitle') }}
          <v-spacer />
          <v-btn icon variant="text" :disabled="importDialog.applying" @click="importDialog.open = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="max-height: 70vh;">
          <div class="text-body-2 text-medium-emphasis mb-3">
            {{ t('admConfig.skillsEditor.importSource', {
              source: importDialog.data?.source || '?',
              date: formatDateTime(importDialog.data?.exportedAt)
            }) }}
          </div>
          <v-alert v-if="importDialog.error" type="error" variant="tonal" density="compact" class="mb-3">
            {{ importDialog.error }}
          </v-alert>
          <v-alert v-if="importDialog.items.some(i => i.targetNewer)" type="warning" variant="tonal"
                   density="compact" class="mb-3">
            {{ t('admConfig.skillsEditor.importTargetNewerHint') }}
          </v-alert>

          <v-table density="compact" class="skills-table">
            <thead>
              <tr>
                <th style="width: 40px;"></th>
                <th>{{ t('admConfig.skillsEditor.colId') }}</th>
                <th>{{ t('admConfig.skillsEditor.colStatus') }}</th>
                <th>{{ t('admConfig.skillsEditor.colSource') }}</th>
                <th>{{ t('admConfig.skillsEditor.colTarget') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in importDialog.items" :key="item.id">
                <td>
                  <v-checkbox-btn
                    v-model="importDialog.selected"
                    :value="item.id"
                    :disabled="!isImportable(item)"
                    density="compact"
                  />
                </td>
                <td><code>{{ item.id }}</code></td>
                <td>
                  <v-chip size="x-small" label variant="tonal" :color="statusColor(item)">
                    {{ t(`admConfig.skillsEditor.status_${item.status}`) }}
                  </v-chip>
                  <v-chip v-if="item.targetNewer" size="x-small" label variant="tonal" color="warning" class="ml-1">
                    {{ t('admConfig.skillsEditor.targetNewer') }}
                  </v-chip>
                  <div v-if="item.message" class="text-caption text-error">{{ item.message }}</div>
                </td>
                <td class="text-caption">{{ formatDateTime(item.sourceUpdatedAt) }}</td>
                <td class="text-caption">{{ formatDateTime(item.targetUpdatedAt) }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <span class="text-caption text-medium-emphasis ml-2">
            {{ t('admConfig.skillsEditor.importHistoryHint') }}
          </span>
          <v-spacer />
          <v-btn variant="text" :disabled="importDialog.applying" @click="importDialog.open = false">
            {{ t('common.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            :loading="importDialog.applying"
            :disabled="!importDialog.selected.length"
            @click="applyImport"
          >
            {{ t('admConfig.skillsEditor.importApply', { count: importDialog.selected.length }) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Editor del testo: scrive direttamente il .md -->
    <SkillEditDialog
      v-model="editor.open"
      :skill-id="editor.skillId"
      @saved="load"
    />

    <!-- Versioni archiviate da editor, import e ripristini: si leggono e si rimettono -->
    <SkillHistoryDialog
      v-model="history.open"
      :skill-id="history.skillId"
      @restored="load"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useProjectStore } from '../stores/projects.js'
import SkillEditDialog from './SkillEditDialog.vue'
import SkillHistoryDialog from './SkillHistoryDialog.vue'
import { asDate } from '../utils/aiRuns.js'

const { t } = useI18n()
const projectStore = useProjectStore()

// Stesso pattern del Backend (SkillIdPattern): il nome è anche il nome del file
const SKILL_ID_PATTERN = /^[A-Za-z0-9._-]+$/

const skills = ref([])
const loading = ref(false)
const error = ref('')
const listDialog = ref(false)
const filter = ref('')

const newSkillId = ref('')
const newSkillError = ref('')

const editor = ref({ open: false, skillId: '' })
const history = ref({ open: false, skillId: '' })

const exporting = ref(false)
const fileInput = ref(null)
const importDone = ref('')
const importDialog = ref({ open: false, data: null, items: [], selected: [], applying: false, error: '' })

const filteredSkills = computed(() => {
  const term = (filter.value || '').trim().toLowerCase()
  if (!term) return skills.value
  return skills.value.filter(s => s.id.toLowerCase().includes(term))
})

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    skills.value = await projectStore.loadSkills()
  } catch (e) {
    error.value = e?.message || t('admConfig.skillsEditor.loadError')
  } finally {
    loading.value = false
  }
}

const openList = async () => {
  listDialog.value = true
  await load()
}

const openEditor = (skillId) => {
  editor.value = { open: true, skillId }
}

const openHistory = (skillId) => {
  history.value = { open: true, skillId }
}

const createSkill = () => {
  const id = newSkillId.value.trim()
  newSkillError.value = ''
  if (!id) return
  if (!SKILL_ID_PATTERN.test(id) || id.includes('..')) {
    newSkillError.value = t('admConfig.skillsEditor.errId')
    return
  }
  if (skills.value.some(s => s.id === id)) {
    newSkillError.value = t('admConfig.skillsEditor.errExists')
    return
  }
  openEditor(id)
  newSkillId.value = ''
}

// ---- esportazione / importazione ----

const exportAll = async () => {
  exporting.value = true
  error.value = ''
  try {
    const data = await projectStore.exportSkills()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    // Nome con origine e data: due export da database diversi non si confondono in Download
    const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '')
    a.href = url
    a.download = `skills-${(data?.source || 'lexsystem').replace(/[^A-Za-z0-9.-]/g, '_')}-${stamp}.json`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    error.value = e?.message || t('admConfig.skillsEditor.exportError')
  } finally {
    exporting.value = false
  }
}

// Nuove e cambiate si possono importare; uguali e non valide no.
const isImportable = item => item.status === 'new' || item.status === 'changed'

const statusColor = item => ({ new: 'success', changed: 'primary', same: undefined, invalid: 'error' }[item.status])

const onImportFile = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = '' // lo stesso file si può riscegliere dopo averlo corretto
  if (!file) return

  error.value = ''
  importDone.value = ''
  let data
  try {
    data = JSON.parse(await file.text())
  } catch {
    error.value = t('admConfig.skillsEditor.importInvalidFile')
    return
  }

  try {
    const preview = await projectStore.importSkills(data)
    const items = preview?.items || []
    importDialog.value = {
      open: true,
      data,
      items,
      // Preselezionate solo quelle sicure: una skill modificata qui dopo l'export la sceglie chi importa
      selected: items.filter(i => isImportable(i) && !i.targetNewer).map(i => i.id),
      applying: false,
      error: ''
    }
  } catch (e) {
    error.value = e?.message || t('admConfig.skillsEditor.importError')
  }
}

const applyImport = async () => {
  const dialog = importDialog.value
  dialog.applying = true
  dialog.error = ''
  try {
    const result = await projectStore.importSkills(dialog.data, { apply: true, ids: dialog.selected })
    const count = result?.applied?.length || 0
    dialog.open = false
    importDone.value = t('admConfig.skillsEditor.importDone', { count })
    await load()
  } catch (e) {
    dialog.error = e?.message || t('admConfig.skillsEditor.importError')
  } finally {
    dialog.applying = false
  }
}

// La data di modifica arriva da SQL in UTC ma senza fuso, come quelle del diario IA: `asDate`
// aggiunge la Z mancante, senza la quale l'orario risulterebbe spostato di tutto il fuso.
const formatDateTime = (iso) => {
  if (!iso) return ''
  const d = asDate(iso)
  return d && !isNaN(d.getTime()) ? d.toLocaleString() : iso
}
</script>

<style scoped>
.skills-table :deep(td) {
  white-space: nowrap;
}
</style>
